/**
 * apiHelper — the single entry point for every HTTP request in the app.
 *
 * Do NOT call `apiClient` / `httpClient` / axios directly from features; go through this helper so
 * URL building, auth, caching, downloads, and error handling stay centralized. Per-endpoint fetch
 * functions live one-per-file in `src/utils/` and wrap this (see e.g. `getUserInfo.js`).
 *
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
 * @param {string} path  Path appended to the resolved API base (e.g. '/user-info/').
 * @param {*} [data]     Request body.
 * @param {object} [config]
 * @param {boolean} [config.noSecure]        Use the non-secure httpClient instead of apiClient.
 * @param {boolean} [config.useGateway]      Base = VUE_APP_GATEWAY_API_URL (adds x-thn-account-id).
 * @param {boolean} [config.usePublicGateway] Base = VUE_APP_PUBLIC_API_URL + '/v2'.
 * @param {boolean} [config.useCache]        Route through the request cache (useRequestCache).
 * @param {'download'} [config.type]         Stream the response as a blob and trigger a file download.
 * @param {string} [config.fileName]         Filename for downloads (else derived from headers).
 * @returns {Promise<import('axios').AxiosResponse>} Resolves on 200/201/202; 401 triggers logout().
 *
 * Default base URL: VUE_APP_BENCHDIRECT_API_URL + '/api/v2'.
 */
import apiClient from '@/modules/apiClient';
import httpClient from '@/modules/httpClient';

import useRequestCache from '@/utils/useRequestCache';
import useUserState from '@/state/useUserState';
import logout from '@/utils/logout';
import getFilenameFromHeaders from '@/utils/getFilenameFromHeaders';

const env = import.meta.env || {};

const { useFetchCache } = useRequestCache();
const { userData } = useUserState();

const validCodes = [200, 201, 202];

const apiHandler = async ({
  method,
  config,
  data,
  url,
  headers,
}) => {
  let response;

  if (config.type !== 'download') {
    const options = {
      url,
      method,
      data,
      signal: config.signal,
      headers,
    };

    const httpRequestFn = config.noSecure ? httpClient.request : apiClient.request;
    const fetchFn = config.useCache ? useFetchCache : httpRequestFn;

    response = await fetchFn(options);
  } else {
    const httpRequestFn = config.noSecure ? httpClient.request : apiClient.request;
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));

    response = await httpRequestFn({
      url,
      method,
      data: formData,
      responseType: 'blob',
    });

    if (response.status !== 200) throw new Error('Download failed');

    const responseUrl = window.URL.createObjectURL(response.data);

    const a = document.createElement('a');

    a.href = responseUrl;

    a.download = config.fileName || getFilenameFromHeaders(response.headers);

    a.click();
  }

  return response;
};

const apiHelper = async (method, path, data, config = {}) => {
  let url;
  const headers = {};

  if (config.useGateway) {
    url = `${env.VUE_APP_GATEWAY_API_URL}${path}`;
    headers['x-thn-account-id'] = userData.accountId;
  } else if (config.usePublicGateway) {
    url = `${env.VUE_APP_PUBLIC_API_URL}/v2${path}`;
  } else {
    url = `${env.VUE_APP_BENCHDIRECT_API_URL}/api/v2${path}`;
  }

  // remove bar at the end of the url (could be copied by mistake)
  url = url.replace(/\/$/, '');

  const response = await apiHandler({
    method,
    url,
    data,
    config,
    headers,
  });

  if (response.status === 401) {
    return logout();
  }

  if (config.type === 'download' || validCodes.indexOf(response.status) >= 0) {
    return response;
  }

  throw new Error(`An error has occured: ${response.status}`);
};

export default apiHelper;
